import React, { useState, useEffect, useCallback } from 'react';
import lpApi from '../../../helpers/lpApi';

// ============================================================================
// Big Projects · Funds Held — admin reconciliation list of escrow funds held:
// captured project payments whose matching payout has NOT yet been transferred.
//
// Consumes besthand-api `listFundsHeld` (returns `payments`) through the
// `/lp-graphql` proxy via `lpApi`. Admin-gated server-side (requireAdmin).
// ============================================================================

const Q_FUNDS = `
  query {
    listFundsHeld {
      status errorMessage
      payments {
        id projectContractId projectMilestoneId stripePaymentIntentId
        amount platformFeeClient currency status isDownPayment
        capturedAt refundedAmount createdAt
      }
    }
  }
`;

// ADMIN action — refund a held milestone payment to the client (full or
// partial). Reuses adminRefundMilestone (refundMilestonePayment primitive).
const M_REFUND = `
  mutation ($milestoneId: String!, $reason: String, $amount: Float) {
    adminRefundMilestone(milestoneId: $milestoneId, reason: $reason, amount: $amount) {
      status errorMessage
      milestone { id status }
      payment { id amount refundedAmount stripeRefundId }
    }
  }
`;

const S = {
  page: { maxWidth: 1080, margin: '0 auto', padding: 24, fontFamily: "'Inter', -apple-system, sans-serif", color: '#0f1214' },
  h2: { margin: '0 0 4px', fontSize: 24, fontWeight: 800 },
  hint: { color: '#6b7280', fontSize: 14, marginBottom: 20 },
  toolbar: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' },
  btnGhost: { background: '#fff', color: '#0f1214', border: '1px solid #e7e9ec', borderRadius: 9, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  totalPill: { background: '#0f1214', color: '#fff', borderRadius: 10, padding: '8px 14px', fontSize: 14, fontWeight: 800 },
  card: { border: '1px solid #e7e9ec', borderRadius: 14, padding: '16px 20px', marginBottom: 12, boxShadow: '0 6px 18px rgba(15,18,20,0.03)' },
  rowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  name: { fontSize: 17, fontWeight: 800, margin: 0 },
  amount: { fontSize: 20, fontWeight: 800, color: '#137a37', whiteSpace: 'nowrap' },
  meta: { color: '#6b7280', fontSize: 13, marginTop: 4, lineHeight: 1.5 },
  badge: (kind) => {
    const map = {
      SUCCEEDED: { bg: '#e7f6ec', fg: '#137a37' },
      PENDING: { bg: '#fff3d6', fg: '#946200' },
      REFUNDED: { bg: '#fde7e8', fg: '#c2151c' },
      DOWN: { bg: '#eef2ff', fg: '#3730a3' },
    };
    const c = map[kind] || { bg: '#eef2f4', fg: '#5a6168' };
    return { fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', padding: '4px 10px', borderRadius: 999, background: c.bg, color: c.fg, whiteSpace: 'nowrap' };
  },
  empty: { color: '#6b7280', fontSize: 14, padding: '40px 0', textAlign: 'center' },
  alert: (ok) => ({ padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 16, background: ok ? '#ecfdf5' : '#fef2f2', color: ok ? '#065f46' : '#991b1b' }),
  actions: { display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' },
  btnRed: { background: '#fff', color: '#c2151c', border: '1px solid #f3c9cb', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
};

const fmtMoney = (v, cur) => (v == null ? '—' : `${cur && cur !== 'USD' ? `${cur} ` : '$'}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtDate = (s) => { if (!s) return ''; try { return new Date(s).toLocaleString(); } catch (e) { return s; } };

function FundsHeld() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [busyId, setBusyId] = useState('');

  const firstError = (res) => (res && res.errors && res.errors.length ? res.errors[0].message : 'Something went wrong.');

  const load = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    try {
      const res = await lpApi(Q_FUNDS);
      if (res.errors) {
        setMsg({ error: firstError(res) });
      } else {
        const env = res.data && res.data.listFundsHeld;
        if (env && env.status === 200) setPayments(env.payments || []);
        else if (env) setMsg({ error: env.errorMessage || `Error ${env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Could not load funds held: ${e.message}` });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const refund = (p) => {
    if (!p.projectMilestoneId) { setMsg({ error: 'This payment has no milestone to refund.' }); return; }
    const ok = typeof window !== 'undefined' && window.confirm(
      `REFUND the client for held payment #${p.id} (milestone #${p.projectMilestoneId}, ${fmtMoney(p.amount, p.currency)})?\n\n`
      + 'This issues a REAL Stripe refund of the held client charge. '
      + 'Leave the amount blank for a FULL refund, or enter a smaller amount for a partial refund.',
    );
    if (!ok) return;
    const raw = window.prompt(`Refund amount in USD (blank = full remaining):`, '');
    if (raw === null) return;
    let amount = null;
    if (raw.trim() !== '') {
      amount = Number(raw);
      if (!Number.isFinite(amount) || amount <= 0) { setMsg({ error: 'Refund amount must be a number greater than 0 (or blank for full).' }); return; }
    }
    const reason = window.prompt(`Reason for the refund (optional):`);
    if (reason === null) return;
    setBusyId(`f${p.id}`);
    setMsg(null);
    lpApi(M_REFUND, { milestoneId: String(p.projectMilestoneId), reason: reason.trim() || null, amount })
      .then(async (res) => {
        if (res.errors) { setMsg({ error: firstError(res) }); setBusyId(''); return; }
        const env = res.data && res.data.adminRefundMilestone;
        if (env && env.status >= 200 && env.status < 300) { setMsg({ ok: `Refund processed for milestone #${p.projectMilestoneId}.` }); await load(); }
        else { setMsg({ error: (env && env.errorMessage) || `Error ${env && env.status}` }); }
        setBusyId('');
      })
      .catch((e) => { setMsg({ error: `Action failed: ${e.message}` }); setBusyId(''); });
  };

  const totalHeld = payments.reduce((sum, p) => sum + (Number(p.amount) || 0) - (Number(p.refundedAmount) || 0), 0);

  return (
    <div style={S.page}>
      <h2 style={S.h2}>Funds Held</h2>
      <div style={S.hint}>
        Large Projects — captured client payments still held in escrow (no payout transferred yet).
        Use this for reconciliation; releases happen on milestone completion.
      </div>

      <div style={S.toolbar}>
        <button style={S.btnGhost} onClick={() => load()} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
        <span style={{ color: '#6b7280', fontSize: 13 }}>{payments.length} held</span>
        {payments.length > 0 && <span style={S.totalPill}>Held: {fmtMoney(totalHeld)}</span>}
      </div>

      {msg && <div style={S.alert(!!msg.ok)}>{msg.ok || msg.error}</div>}
      {loading && <div style={S.empty}>Loading…</div>}

      {!loading && (
        payments.length === 0 ? (
          <div style={S.empty}>No funds currently held in escrow.</div>
        ) : payments.map((p) => (
          <div style={S.card} key={p.id}>
            <div style={S.rowTop}>
              <div>
                <p style={S.name}>
                  Payment #{p.id}
                  {p.isDownPayment ? <span style={{ ...S.badge('DOWN'), marginLeft: 8 }}>DOWN PAYMENT</span> : null}
                </p>
                <div style={S.meta}>
                  Contract #{p.projectContractId}
                  {p.projectMilestoneId ? ` · Milestone #${p.projectMilestoneId}` : ''}
                  {p.platformFeeClient != null ? ` · Platform fee ${fmtMoney(p.platformFeeClient, p.currency)}` : ''}
                  {p.refundedAmount ? ` · Refunded ${fmtMoney(p.refundedAmount, p.currency)}` : ''}
                </div>
                <div style={S.meta}>
                  {p.stripePaymentIntentId ? `${p.stripePaymentIntentId} · ` : ''}
                  Captured {p.capturedAt ? fmtDate(p.capturedAt) : '—'}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={S.amount}>{fmtMoney(p.amount, p.currency)}</div>
                <span style={S.badge(p.status)}>{p.status || '—'}</span>
              </div>
            </div>
            {p.status === 'SUCCEEDED' && (Number(p.amount) - Number(p.refundedAmount || 0)) > 0 && (
              <div style={S.actions}>
                <button style={S.btnRed} disabled={busyId === `f${p.id}`} onClick={() => refund(p)}>
                  {busyId === `f${p.id}` ? '…' : 'Refund client'}
                </button>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

export default FundsHeld;
